import type { Location } from 'react-router-dom';
import { ROUTE_EXCLUDE_PREFIXES } from '../constants';

type ReturnLocationState = {
  returnTo?: unknown;
};

const isSafeInternalPath = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  // Browsers normalize backslashes and strip control characters from URLs.
  // A seemingly relative /\\host or /<tab>/host can therefore become external.
  const hasUnsafeCharacter = Array.from(trimmed).some((character) => {
    const code = character.charCodeAt(0);
    return character === '\\' || code < 32 || code === 127;
  });
  return trimmed.startsWith('/')
    && !trimmed.startsWith('//')
    && !hasUnsafeCharacter;
};

const withRemainingSearchParams = (path: string, searchParams: URLSearchParams) => {
  const query = searchParams.toString();
  if (!query.length) return path;

  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${query}`;
};

export function getPostAuthenticationRoute(
  location: Pick<Location, 'pathname' | 'search' | 'hash' | 'state'>,
  fallback = '/products',
): string {
  const returnTo = (location.state as ReturnLocationState | null)?.returnTo;
  if (isSafeInternalPath(returnTo)) {
    return returnTo.trim();
  }

  const searchParams = new URLSearchParams(location.search || '');
  const redirectRoute = searchParams.get('redirectRoute');
  if (isSafeInternalPath(redirectRoute)) {
    searchParams.delete('redirectRoute');
    return withRemainingSearchParams(redirectRoute.trim(), searchParams);
  }

  const currentPath = `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
  if (
    isSafeInternalPath(currentPath)
    && !ROUTE_EXCLUDE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))
  ) {
    return currentPath;
  }

  return fallback;
}
